import { useState, useEffect, useMemo, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet } from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import CustomText from "../components/common/general/CustomText/CustomText";
import {
  CourseWithMetadata,
  useCompleteDegreeInvitationMutation,
  useGetAvailableCoursesForStudentQuery,
  useGetEnrolledCoursesForStudentQuery,
  useGetDegreeForUserWithEnrolledUserInfoQuery,
} from "@axio/graphql/generated/graphql";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import Loader from "../components/common/status/Loader/Loader";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import { MotiView } from "moti";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAppConfig } from "../AppConfigProvider";
import TabNavigator from "../components/customCoursework/CourseCatalogue/TabNavigator/TabNavigator";
import { useCourseDetails } from "../contexts/CourseDetailsContext";
import CatalogueCard from "../components/customCoursework/CourseCatalogue/CatalogueCard/CatalogueCard";
import usePagination from "../hooks/usePagination";
import TableControlsContainer from "../containers/general/TableControls/TableControlsContainer";
import Pagination from "../components/common/TableControls/Pagination/Pagination";
import DefaultLayout from "../layouts/DefaultLayout.web";
import SortingDropdown from "../components/common/TableControls/SortingDropdown/SortingDropdown";
import type { RootStackParamList } from "../navigation/navigation.types";
import { useAuth } from "../hooks/useAuth";

const tabs = [
  {
    key: "all_courses",
    title: "All Courses",
  },
  {
    key: "enrolled_courses",
    title: "Enrolled Courses",
  },
];

const dropdownItems = [
  // { label: "Price (Low to High)", value: "price-asc" },
  // { label: "Price (High to Low)", value: "price-desc" },
  { label: "Name (A → Z)", value: "name-asc" },
  { label: "Name (Z → A)", value: "name-desc" },
];

const ITEMS_PER_PAGE = 50;

const CustomSchoolStudentCoursesView = () => {
  const [activeTab, setActiveTab] = useState("all_courses");
  const [sortBy, setSortBy] = useState("name-asc");

  const [pageTitle, setPageTitle] = useState("My Courses");

  const [selectedDegree, setSelectedDegree] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Dropdown menus
  const [sortingDropdownMenuVisible, setSortingDropdownMenuVisible] = useState(false);
  const [degreeDropdownVisible, setDegreeDropdownVisible] = useState(false);

  const [degreeItems, setDegreeItems] = useState([]);
  const { setIsVisible, setCourseData, onEnroll, loading } = useCourseDetails();

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const appConfig = useAppConfig();
  const integrationId = appConfig.axioSchools[0];
  const hasDegrees = appConfig.hasDegrees;
  const { isMedium, isLarge } = useResponsiveScreen();

  const { userInfo } = useAuth();

  const { page, setPage, pageSize, setPageSize } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const [completeDegreeInvitation] = useCompleteDegreeInvitationMutation();

  const {
    data: courses,
    loading: coursesLoading,
    error: coursesError,
  } = useGetAvailableCoursesForStudentQuery({
    variables: {
      schoolId: integrationId,
      degreeId: selectedDegree,
      page,
      pageSize,
    },
    skip: !integrationId || activeTab !== "all_courses",
  });

  const {
    data: enrolledCoursesData,
    loading: enrolledCoursesLoading,
    error: enrolledCoursesError,
  } = useGetEnrolledCoursesForStudentQuery({
    variables: {
      schoolId: integrationId,
      degreeId: selectedDegree,
      page,
      pageSize,
    },
    skip: !integrationId || activeTab !== "enrolled_courses",
    fetchPolicy: "cache-and-network",
  });

  const { data: degreeData } = useGetDegreeForUserWithEnrolledUserInfoQuery({
    skip: !userInfo.userId,
  });

  useEffect(() => {
    const studentHasDegrees = degreeData?.getDegreeForUserWithEnrolledUserInfo?.data?.length > 0;
    if (studentHasDegrees) {
      const degreeDropdownItems = [{ label: "All Courses", value: "" }];

      degreeData?.getDegreeForUserWithEnrolledUserInfo?.data?.forEach((degree) => {
        degreeDropdownItems.push({ label: degree.name, value: degree.id });
      });
      setDegreeItems(degreeDropdownItems);

      const pendingInviteDegree = degreeData?.getDegreeForUserWithEnrolledUserInfo?.data?.find((degree) =>
        degree.usersEnrolledInConnection?.edges?.some(
          (edge) => edge?.properties?.pendingDegreeEnrollmentInvite === true && edge?.node?.id === userInfo.userId
        )
      );

      if (pendingInviteDegree) {
        setSelectedDegree(pendingInviteDegree.id);
        completeDegreeInvitation({ variables: { degreeId: pendingInviteDegree.id } });
      } else {
        setSelectedDegree(degreeData?.getDegreeForUserWithEnrolledUserInfo?.data[0]?.id || "");
      }
    }
  }, [degreeData?.getDegreeForUserWithEnrolledUserInfo?.data]);

  useEffect(() => {
    setPageTitle(
      degreeData?.getDegreeForUserWithEnrolledUserInfo?.data?.find((degree) => degree.id === selectedDegree)?.name ||
        "My Courses"
    );
  }, [selectedDegree]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedDegree, setPage]);

  const totalItemsCount =
    activeTab === "enrolled_courses"
      ? enrolledCoursesData?.getEnrolledCoursesForStudent?.data?.totalCount || 0
      : courses?.getAvailableCoursesForStudent?.data?.totalCount || 0;

  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture("Course Catalogue Page Visited");
  }, []);

  const handleDetailsPress = (course, name, preRequisitesCompleted) => {
    setCourseData({
      ...course,
      name,
      preRequisitesCompleted,
      modules: course?.modules?.map((module) => ({
        name: module.title,
        activities: [
          ...(module?.assignments?.map((assignment) => ({
            name: assignment.name,
            id: assignment.id,
          })) || []),
          ...(module?.aiTutors?.map((aiTutor) => ({
            name: aiTutor.title,
            id: aiTutor.id,
          })) || []),
          ...(module?.axioResources?.map((resource) => ({
            name: resource.name,
            id: resource.id,
          })) || []),
        ],
      })),
    });
    setIsVisible(true);
  };

  const handleEnrollPress = (courseId, hasCost) => {
    setSelectedCourse(courseId);

    onEnroll(courseId, hasCost, () => {
      navigation.navigate("Custom School Student Courses", {
        integration_id: integrationId,
      });
    });
  };

  const courseTypeMap = useMemo(() => {
    const map = new Map<string, CourseWithMetadata[]>();
    const allCourses = (courses?.getAvailableCoursesForStudent?.data?.courses || []) as CourseWithMetadata[];
    const enrolled = (enrolledCoursesData?.getEnrolledCoursesForStudent?.data?.courses || []) as CourseWithMetadata[];
    map.set("all_courses", allCourses);
    map.set("enrolled_courses", enrolled);

    return map;
  }, [
    courses?.getAvailableCoursesForStudent?.data?.courses,
    enrolledCoursesData?.getEnrolledCoursesForStudent?.data?.courses,
  ]);

  const filteredData = courseTypeMap.get(activeTab);

  const filteredByDegree = useMemo(() => {
    if (selectedDegree === "") {
      return filteredData;
    }

    return filteredData.filter(
      (course) =>
        course.course?.degreesHasCourse &&
        course.course?.degreesHasCourse.some((degree) => degree.id === selectedDegree)
    );
  }, [filteredData, selectedDegree]);

  const sortedData = useMemo(() => {
    return [...filteredByDegree].sort((a, b) => {
      switch (sortBy) {
        case "price-asc":
          return a.course?.cost - b.course?.cost;
        case "price-desc":
          return b.course?.cost - a.course?.cost;
        case "name-asc":
          return a.course?.name.localeCompare(b.course?.name);
        case "name-desc":
          return b.course?.name.localeCompare(a.course?.name);
        default:
          return 0;
      }
    });
  }, [filteredByDegree, sortBy]);

  const handleOpenDegreeDropdown = useCallback((value: boolean) => {
    setDegreeDropdownVisible(value);
    setSortingDropdownMenuVisible(false);
  }, []);

  const handleOpenDropdownMenu = useCallback((value: boolean) => {
    setSortingDropdownMenuVisible(value);
    setDegreeDropdownVisible(false);
  }, []);

  const handleContinueCoursePress = (id) => {
    navigation.navigate("Custom Course Student", {
      course_id: id,
      integration_id: integrationId,
    });
  };

  const isLoading = activeTab === "enrolled_courses" ? enrolledCoursesLoading : coursesLoading;
  const isError = activeTab === "enrolled_courses" ? enrolledCoursesError : coursesError;

  if (isLoading) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </DefaultLayout>
    );
  }
  if (isError) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}
        style={{ zIndex: 5000 }}>
        <View style={{ flexDirection: "column", justifyContent: "flex-start" }}>
          {degreeData?.getDegreeForUserWithEnrolledUserInfo?.data?.length > 0 && (
            <>
              <SortingDropdown
                sortBy={selectedDegree}
                setSortBy={setSelectedDegree}
                sortDropdownOpen={degreeDropdownVisible}
                setSortDropdownOpen={handleOpenDegreeDropdown}
                sortItems={degreeItems}
                label="Active Program"
                customStyle={{ minWidth: 120 }}
                accessibilityLabel="Active program dropdown"
              />
              <CustomSpacing type="vertical" size="l" />
            </>
          )}
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
            <CustomText
              text={pageTitle}
              size={isMedium ? "m" : "xs"}
              textType="display"
              weight="ultraBold"
              role="heading"
              accessibilityRole="header"
              aria-level={1}
              style={{ letterSpacing: -0.72, color: "#101828" }}
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          {selectedDegree === "" && (
            <CustomText
              text="Discover courses tailored to your goals. Enroll in the classes that help you level up your skills."
              size="m"
              weight="normal"
              style={{ color: "#667085" }}
            />
          )}
        </View>
      </MotiView>
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />
      <View style={isLarge ? styles.filteringContainerMedium : styles.filteringContainer}>
        {hasDegrees ? (
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
            tabPanelIdPrefix="course-panel-"
          />
        ) : (
          <View />
        )}
        <View style={styles.filtersContainer}>
          <SortingDropdown
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDropdownOpen={sortingDropdownMenuVisible}
            setSortDropdownOpen={handleOpenDropdownMenu}
            sortItems={dropdownItems}
            label="Sort by"
            accessibilityLabel="Sort by dropdown"
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.divider} />
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />

      <View
        role="tabpanel"
        // @ts-expect-error - React Native accessibilityRole does not include tabpanel
        accessibilityRole="tabpanel"
        aria-labelledby={`course-tab-${activeTab}`}
        id={`course-panel-${activeTab}`}>
        {totalItemsCount > ITEMS_PER_PAGE && (
          <Pagination page={page} handlePageChange={setPage} totalPageCount={Math.ceil(totalItemsCount / pageSize)} />
        )}
        <CustomSpacing type="vertical" size="m" />

        <View
          role="list"
          accessibilityRole="list"
          aria-label="Course cards"
          style={isMedium ? styles.gridContainer : styles.flexContainer}>
          {sortedData.map((course, index) => {
            return (
              <CatalogueCard
                key={index}
                course={course.course}
                onEnrollPress={() => handleEnrollPress(course.course?.id, !!course.course?.cost)}
                onDetailsPress={handleDetailsPress}
                isLoading={selectedCourse === course.course?.id && loading}
                progress={course.progress}
                preRequisitesCompleted={course.preRequisitesCompleted}
                handleContinueCoursePress={() => handleContinueCoursePress(course.course?.id)}
              />
            );
          })}
        </View>

        <CustomSpacing type="vertical" size="m" />
        {totalItemsCount > ITEMS_PER_PAGE && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  gridContainer: {
    // @ts-expect-error - it works for web
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
  flexContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  divider: {
    height: 1,
    backgroundColor: "#EAECF0",
  },
  filteringContainerMedium: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    rowGap: 0,
  },
  filteringContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    rowGap: 12,
  },
  filtersContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    columnGap: 16,
    zIndex: 3000,
  },
});

export default CustomSchoolStudentCoursesView;
